import { useForm, yupResolver } from "@mantine/form";
import {
  useUpdateProductMutation,
  useCreateProductMutation,
  CreateProductInput,
  useGetProductLazyQuery,
  useDeleteProductMutation,
  GetProductGroupQuery,
} from "src/generated/graphql";
import { memo } from "react";
import ProductFormFields from "../formFields";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import {
  Button,
  Flex,
  Group,
  Title,
  Badge,
  useMantineTheme,
} from "@mantine/core";
import { useEffect } from "react";
import { GetProductGroup } from "src/api/graphql/queries/Product";
import { array, boolean, number, object, string } from "yup";
import { IvariantMetrics } from "src/api/context/ProductContext";

export interface IProductForm
  extends Omit<
    CreateProductInput,
    "revenueCategory" | "tags" | "metricValues" | "taxRates"
  > {
  revenueCategory?: string;
  tags?: string[];
  id?: string;
  metricValues?: {};
  taxRates?: string[];
}

type IForm = NonNullable<
  GetProductGroupQuery["productGroupOne"]
>["products"][0];

interface ProductDetailProps {
  formData: IForm;
  handleModal?: (product: IForm) => void;
  isModal?: boolean;
  groupId?: string;
  variantMetrics: IvariantMetrics;
  revenueCategoryId: string | undefined;
  locationId: string | undefined;
  isAllowWrite: boolean;
}

const ProductDetail = ({
  formData,
  isModal,
  handleModal,
  groupId,
  variantMetrics,
  revenueCategoryId,
  locationId,
  isAllowWrite,
}: ProductDetailProps) => {
  const modals = useModals();
  const theme = useMantineTheme();
  const refetchQuery = [
    {
      query: GetProductGroup,
      variables: { productGroupOneId: groupId },
    },
  ];

  useEffect(() => {
    formData &&
      isModal &&
      getProduct({
        variables: { id: formData.id },
      });
  }, [formData, isModal]);

  const [getProduct] = useGetProductLazyQuery({
    onCompleted(data) {
      if (!data.productOne) return;
      const { revenue, tags, metricValues, taxRates, ...rest } =
        data.productOne;
      let _data = {
        revenueCategory: revenue?.id,
        tags: tags?.map((val) => val.id),
        metricValues: metricValues?.reduce(
          (acc, cur) => ({ ...acc, [cur.metric?.name!]: cur.id }),
          {}
        ),
        taxRates: taxRates?.map((val) => val.id),
        ...rest,
      };
      form.setValues(_data);
      form.resetDirty(_data);
    },
    fetchPolicy: "network-only",
  });

  const [createProduct, { loading: createLoading }] = useCreateProductMutation({
    onCompleted: () => {
      modals.closeAll();
    },
    refetchQueries: refetchQuery,
  });

  const [updateProduct, { loading: updateLoading }] = useUpdateProductMutation({
    onCompleted: () => {
      modals.closeAll();
    },
    refetchQueries: refetchQuery,
  });
  const [deleteProduct, { loading: deleteLoading }] = useDeleteProductMutation({
    refetchQueries: refetchQuery,
  });

  const form = useForm<IProductForm>({
    initialValues: {
      name: "",
      description: "",
      revenueCategory: "",
      tags: [],
      cost: 0,
      stock: 0,
      barcode: "",
      imgUrl: "",
      isTaxable: false,
      isActive: false,
      sku: "",
      isCommissionable: false,
      isCommissionPercent: false,
      isPostTax: false,
      taxRates: [],
      metricValues:
        variantMetrics?.reduce(
          (acc, cur) => ({ ...acc, [cur.name]: "" }),
          {}
        ) || {},
    },
    validate: yupResolver(
      object().shape({
        name: string()
          .trim("No spaces allowed")
          .strict(true)
          .min(2, "Name should must be least 2 letters")
          .max(30, "Name must not be greater than 30 letters")
          .required("Name is required"),
        description: string().max(200).notRequired(),
        barcode: string().max(200).notRequired(),
        sku: string().required("SKU is required").max(30),
        tags: array(),
        mode: string(),
        commissionValue: number().when("isCommissionable", {
          is: true,
          then: () => number().required("Value is required"),
          otherwise: () => number().nullable(),
        }),
        isTaxable: boolean(),
        taxRates: array().when("isTaxable", {
          is: (isTaxable: boolean) => !!isTaxable,
          then: () =>
            array()
              .min(1, "at least 1 Tax Rate is required")
              .required("Tax Rate is required"),
          otherwise: () => array(),
        }),
        metricValues: object().shape({
          ...Object.fromEntries(
            variantMetrics!.map((e) => [
              e.name,
              string().required(`${e.name} is Required`),
            ])
          ),
        }),
      })
    ),
  });

  const handleDelete = () => {
    deleteProduct({
      variables: { deleteProductId: formData?.id! },
    });
  };

  const handleSubmit = async (values: Required<IProductForm>) => {
    const {
      id,
      tags,
      commissionValue,
      isCommissionPercent,
      metricValues,
      revenueCategory,
      taxRates,
      ...rest
    } = values;
    if (!revenueCategoryId) return;
    let _obj: CreateProductInput = {
      revenueCategory: { id: revenueCategoryId },
      tags: tags?.map((service) => ({ id: service })),
      ...(rest.isCommissionable
        ? { commissionValue, isCommissionPercent }
        : {
            commissionValue: null,
            isCommissionPercent: false,
          }),
      ...(formData?.id ? {} : { productGroup: { id: groupId } }),
      ...(Object.keys(metricValues).length && {
        metricValues: Object.values(metricValues).map((e) => ({
          id: e as string,
        })),
      }),
      ...(rest.isTaxable
        ? { taxRates: taxRates?.map((e) => ({ id: e })) }
        : {
            taxRates: [],
          }),
      ...rest,
    };

    formData?.id
      ? updateProduct({
          variables: {
            data: _obj,
            id: formData?.id,
          },
        })
      : createProduct({
          variables: {
            data: _obj,
          },
        });
  };

  const handleEdit = () => {
    handleModal && handleModal(formData);
  };

  return (
    <>
      <FormCard
        title={""}
        titleContent={
          <Flex align={"center"}>
            <Title order={5} weight={700}>
              {`\u00A0`}
              {formData?.name.length > 15
                ? `${formData.name.slice(0, 15)}...`
                : formData?.name}{" "}
              {`\u00A0`}
            </Title>
            <Badge
              color={formData?.stock ? "gray" : "red"}
              ml={"xs"}
              size="sm"
              sx={{ textTransform: "none" }}
            >
              {formData?.stock ? "In-Stock" : "Out-of-Stock"}
            </Badge>
            {variantMetrics?.length !== formData?.metricValues.length ? (
              <Badge
                color="red"
                ml={"xs"}
                size="md"
                sx={{ textTransform: "none" }}
              >
                Please Add Variant Value
              </Badge>
            ) : null}
          </Flex>
        }
        loading={updateLoading || deleteLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) =>
          handleSubmit(values as Required<IProductForm>)
        )}
        startImage={formData?.imgUrl || "NA"}
        hideCard={isModal}
        mb={10}
      >
        {isModal && (
          <>
            <ProductFormFields
              form={form}
              variantMetrics={variantMetrics}
              locationId={locationId}
            />
            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={() => modals.closeAll()}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={createLoading || updateLoading}
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={createLoading || updateLoading}
              >
                {formData?.id ? "Save" : "Create"}
              </Button>
            </Group>
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(ProductDetail);
